/**
 * Password Strength Indicator Component
 *
 * ISO 27001 A.9.4.3 - Système de gestion des mots de passe
 * Affiche en temps réel la force du mot de passe avec zxcvbn
 *
 * Utilisation:
 * <PasswordStrengthIndicator
 *   password={password}
 *   userInputs={[email, fullName]}
 *   isAdmin={false}
 *   showRequirements={true}
 * />
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import zxcvbn from 'zxcvbn';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Check,
  X,
  AlertCircle,
  Lightbulb
} from 'lucide-react';

export interface PasswordStrengthIndicatorProps {
  /** Le mot de passe à évaluer */
  password: string;
  /** Informations utilisateur à éviter (email, nom, etc.) */
  userInputs?: string[];
  /** Si true, applique les exigences admin (plus strictes) */
  isAdmin?: boolean;
  /** Afficher les exigences de la politique */
  showRequirements?: boolean;
  /** Afficher les suggestions d'amélioration */
  showSuggestions?: boolean;
  /** Classe CSS additionnelle */
  className?: string;
}

// Configuration alignée avec le backend
const DEFAULT_POLICY = {
  minLength: 12,
  minScore: 3,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: false,
};

const ADMIN_POLICY = {
  ...DEFAULT_POLICY,
  minLength: 14,
  minScore: 4,
};

// Traductions des warnings zxcvbn
const WARNING_TRANSLATIONS: Record<string, string> = {
  'Straight rows of keys are easy to guess': 'Les suites de touches clavier sont faciles à deviner',
  'Short keyboard patterns are easy to guess': 'Les motifs clavier courts sont faciles à deviner',
  'Repeats like "aaa" are easy to guess': 'Les répétitions comme "aaa" sont faciles à deviner',
  'Repeats like "abcabcabc" are only slightly harder to guess than "abc"':
    'Les répétitions comme "abcabcabc" sont à peine plus difficiles',
  'Sequences like "abc" or "6543" are easy to guess': 'Les séquences comme "abc" ou "6543" sont faciles à deviner',
  'Recent years are easy to guess': 'Les années récentes sont faciles à deviner',
  'Dates are often easy to guess': 'Les dates sont souvent faciles à deviner',
  'This is a top-10 common password': 'C\'est l\'un des 10 mots de passe les plus communs',
  'This is a top-100 common password': 'C\'est l\'un des 100 mots de passe les plus communs',
  'This is a very common password': 'C\'est un mot de passe très commun',
  'This is similar to a commonly used password': 'C\'est similaire à un mot de passe commun',
  'A word by itself is easy to guess': 'Un mot seul est facile à deviner',
  'Names and surnames by themselves are easy to guess': 'Les noms et prénoms seuls sont faciles à deviner',
  'Common names and surnames are easy to guess': 'Les noms et prénoms communs sont faciles à deviner',
};

// Traductions des suggestions zxcvbn
const SUGGESTION_TRANSLATIONS: Record<string, string> = {
  'Use a few words, avoid common phrases': 'Utilisez plusieurs mots, évitez les expressions courantes',
  'No need for symbols, digits, or uppercase letters':
    'Les symboles et majuscules ne sont pas obligatoires si le mot de passe est long',
  'Add another word or two. Uncommon words are better.':
    'Ajoutez un ou deux mots. Les mots peu communs sont préférables.',
  'Capitalization doesn\'t help very much': 'Les majuscules n\'aident pas beaucoup',
  'All-uppercase is almost as easy to guess as all-lowercase':
    'Tout en majuscules est aussi facile à deviner que tout en minuscules',
  'Reversed words aren\'t much harder to guess': 'Les mots inversés ne sont pas plus difficiles à deviner',
  'Predictable substitutions like "@" instead of "a" don\'t help very much':
    'Les substitutions comme "@" au lieu de "a" n\'aident pas beaucoup',
  'Use a longer keyboard pattern with more turns':
    'Utilisez un motif clavier plus long avec plus de directions',
  'Avoid repeated words and characters': 'Évitez les mots et caractères répétés',
  'Avoid sequences': 'Évitez les séquences',
  'Avoid recent years': 'Évitez les années récentes',
  'Avoid years that are associated with you': 'Évitez les années qui vous sont associées',
  'Avoid dates and years that are associated with you':
    'Évitez les dates et années qui vous sont associées',
};

interface StrengthResult {
  score: number;
  feedback: string[];
  suggestions: string[];
  crackTime: string;
  isValid: boolean;
  requirements: {
    label: string;
    met: boolean;
  }[];
}

function analyzePassword(
  password: string,
  userInputs: string[],
  isAdmin: boolean,
  t: TFunction
): StrengthResult {
  const policy = isAdmin ? ADMIN_POLICY : DEFAULT_POLICY;

  if (!password) {
    return {
      score: 0,
      feedback: [],
      suggestions: [],
      crackTime: 'instant',
      isValid: false,
      requirements: getRequirements(policy, '', false, t),
    };
  }

  // Analyse zxcvbn
  const result = zxcvbn(password, userInputs.filter(Boolean));

  // Vérification des exigences
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasMinLength = password.length >= policy.minLength;
  const hasMinScore = result.score >= policy.minScore;

  // Construire les feedbacks
  const feedback: string[] = [];

  if (!hasMinLength) {
    feedback.push(t('auth.passwordStrength.minCharsRequired', { count: policy.minLength }));
  }
  if (policy.requireUppercase && !hasUppercase) {
    feedback.push(t('auth.passwordStrength.uppercaseRequired'));
  }
  if (policy.requireLowercase && !hasLowercase) {
    feedback.push(t('auth.passwordStrength.lowercaseRequired'));
  }
  if (policy.requireNumbers && !hasNumbers) {
    feedback.push(t('auth.passwordStrength.numberRequired'));
  }
  if (policy.requireSpecialChars && !hasSpecialChars) {
    feedback.push(t('auth.passwordStrength.specialCharRequired'));
  }

  // Ajouter warning zxcvbn traduit
  if (result.feedback.warning) {
    const translated = WARNING_TRANSLATIONS[result.feedback.warning] || result.feedback.warning;
    feedback.push(translated);
  }

  // Traduction des suggestions
  const suggestions = result.feedback.suggestions.map(
    s => SUGGESTION_TRANSLATIONS[s] || s
  );

  // Score insuffisant ?
  if (feedback.length === 0 && !hasMinScore) {
    feedback.push(t('auth.passwordStrength.insufficientStrength', { score: result.score, required: policy.minScore }));
    suggestions.push(t('auth.passwordStrength.addRandomWords'));
  }

  const isValid = hasMinLength && hasMinScore &&
    (!policy.requireUppercase || hasUppercase) &&
    (!policy.requireLowercase || hasLowercase) &&
    (!policy.requireNumbers || hasNumbers) &&
    (!policy.requireSpecialChars || hasSpecialChars);

  return {
    score: result.score,
    feedback,
    suggestions,
    crackTime: String(result.crack_times_display.offline_slow_hashing_1e4_per_second),
    isValid,
    requirements: getRequirements(policy, password, result.score >= policy.minScore, t),
  };
}

function getRequirements(
  policy: typeof DEFAULT_POLICY,
  password: string,
  hasMinScore: boolean,
  t: TFunction
) {
  return [
    {
      label: t('auth.passwordStrength.minChars', { count: policy.minLength }),
      met: password.length >= policy.minLength,
    },
    {
      label: t('auth.passwordStrength.minScore', { score: policy.minScore }),
      met: hasMinScore,
    },
    ...(policy.requireUppercase ? [{
      label: t('auth.passwordStrength.atLeastUppercase'),
      met: /[A-Z]/.test(password),
    }] : []),
    ...(policy.requireLowercase ? [{
      label: t('auth.passwordStrength.atLeastLowercase'),
      met: /[a-z]/.test(password),
    }] : []),
    ...(policy.requireNumbers ? [{
      label: t('auth.passwordStrength.atLeastNumber'),
      met: /[0-9]/.test(password),
    }] : []),
    ...(policy.requireSpecialChars ? [{
      label: t('auth.passwordStrength.atLeastSpecial'),
      met: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }] : []),
  ];
}

// Couleurs et icones par score (labels récupérés via t() dans le composant)
const SCORE_CONFIG = {
  0: {
    color: 'bg-gray-200',
    textColor: 'text-gray-500',
    labelKey: 'auth.passwordStrength.veryWeak',
    Icon: ShieldX,
    iconColor: 'text-gray-400'
  },
  1: {
    color: 'bg-red-500',
    textColor: 'text-red-600',
    labelKey: 'auth.passwordStrength.weak',
    Icon: ShieldAlert,
    iconColor: 'text-red-500'
  },
  2: {
    color: 'bg-orange-500',
    textColor: 'text-orange-600',
    labelKey: 'auth.passwordStrength.medium',
    Icon: ShieldAlert,
    iconColor: 'text-orange-500'
  },
  3: {
    color: 'bg-teal-500',
    textColor: 'text-teal-600',
    labelKey: 'auth.passwordStrength.good',
    Icon: Shield,
    iconColor: 'text-teal-500'
  },
  4: {
    color: 'bg-green-500',
    textColor: 'text-green-600',
    labelKey: 'auth.passwordStrength.excellent',
    Icon: ShieldCheck,
    iconColor: 'text-green-500'
  },
} as const;

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  userInputs = [],
  isAdmin = false,
  showRequirements = true,
  showSuggestions = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const analysis = useMemo(
    () => analyzePassword(password, userInputs, isAdmin, t),
    [password, userInputs, isAdmin, t]
  );

  const config = SCORE_CONFIG[analysis.score as keyof typeof SCORE_CONFIG];
  const { Icon, iconColor, color, textColor, labelKey } = config;
  const label = t(labelKey);

  // Ne rien afficher si pas de mot de passe
  if (!password) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Barre de force */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${iconColor}`} />
            <span className={`text-sm font-medium ${textColor}`}>
              {label}
            </span>
          </div>
          <span className="text-xs text-gray-500">
            {t('auth.passwordStrength.score', { score: analysis.score })}
          </span>
        </div>

        {/* Barre de progression */}
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((index) => (
            <div
              key={index}
              className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                index < analysis.score
                  ? color
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Temps de crack */}
        {analysis.crackTime && analysis.score > 0 && (
          <p className="text-xs text-gray-500">
            {t('auth.passwordStrength.crackTime')} {analysis.crackTime}
          </p>
        )}
      </div>

      {/* Exigences de la politique */}
      {showRequirements && (
        <div className="bg-gray-50/50 rounded-xl p-3 space-y-1.5">
          <p className="text-xs font-medium text-gray-600 mb-2">
            {isAdmin ? t('auth.passwordStrength.requirementsAdmin') : t('auth.passwordStrength.requirements')}
          </p>
          {analysis.requirements.map((req, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-2 text-xs ${
                req.met ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {req.met ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <X className="w-3.5 h-3.5 text-gray-400" />
              )}
              <span>{req.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Feedbacks/Avertissements */}
      {analysis.feedback.length > 0 && (
        <div className="bg-red-50/50 border border-red-100 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              {analysis.feedback.map((fb, idx) => (
                <p key={idx} className="text-xs text-red-700">
                  {fb}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {showSuggestions && analysis.suggestions.length > 0 && (
        <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-xs font-medium text-teal-700 mb-1">{t('auth.passwordStrength.suggestions')}</p>
              {analysis.suggestions.map((sug, idx) => (
                <p key={idx} className="text-xs text-teal-700">
                  • {sug}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de validité */}
      {analysis.isValid && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50/50 rounded-xl p-2.5">
          <ShieldCheck className="w-4 h-4" />
          <span className="text-xs font-medium">{t('auth.passwordStrength.validPassword')}</span>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
